import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { contactsQuery } from '../lib/contacts.js';
import { useDebounced } from '../lib/useDebounced.js';

/**
 * Kommandopalette (Strg+K).
 *
 * Der eigentliche Zweck ist nicht Geschwindigkeit für Geübte, sondern
 * Auffindbarkeit: eine Anwendung mit acht Bereichen und je einem Dutzend
 * Aktionen hat mehr Funktionen, als eine Navigationsleiste zeigen kann. Wer
 * weiß, wonach er sucht, tippt es — statt sich durch Menüs zu klicken.
 *
 * Enthält auch die Kontaktsuche: „Wo war noch mal diese Adresse" ist die mit
 * Abstand häufigste Frage im Alltag.
 *
 * Der Dialog wird erst beim Öffnen gemountet: jeder Aufruf beginnt mit
 * frischem Zustand, ohne dass irgendetwas zurückgesetzt werden müsste.
 */

interface Ziel {
  id: string;
  label: string;
  hint?: string;
  to: string;
  params?: Record<string, string>;
}

export interface SlashCommand {
  command: string;
  label: string;
  description: string;
  to: string;
  params?: Record<string, string>;
}

export const SLASH_COMMANDS: SlashCommand[] = [
  { command: '/kampagne', label: 'Neue Kampagne', description: 'E-Mail-Kampagne oder A/B-Test anlegen', to: '/kampagnen/neu' },
  { command: '/kontakt', label: 'Kontakte importieren', description: 'CSV-Import für neue Kontakte', to: '/kontakte/import' },
  { command: '/segment', label: 'Neues Segment', description: 'Zielgruppe mit Filtern definieren', to: '/segmente/neu' },
  { command: '/journey', label: 'Journeys & Automation', description: 'Automatische Nachfass-Ketten & Workflows', to: '/journeys' },
  { command: '/deal', label: 'CRM & Deals', description: 'Verkaufstafel & Split-View öffnen', to: '/crm' },
  { command: '/firma', label: 'Firmen', description: 'Unternehmensdatenbank & Google-Profile', to: '/firmen' },
  { command: '/export', label: 'Auswertung', description: 'Berichte & Klick-Analysen exportieren', to: '/auswertung' },
  { command: '/zustellbarkeit', label: 'Zustellbarkeit', description: 'SPF, DKIM und DMARC Einträge prüfen', to: '/zustellbarkeit' },
  { command: '/dubletten', label: 'Dubletten', description: 'Doppelte Kontakte bereinigen', to: '/kontakte/dubletten' },
];

const ORTE: Ziel[] = [
  { id: 'dashboard', label: 'Übersicht', to: '/' },
  { id: 'contacts', label: 'Kontakte', to: '/kontakte' },
  { id: 'firmen', label: 'Firmen', to: '/firmen' },
  { id: 'import', label: 'Kontakte importieren', hint: 'CSV', to: '/kontakte/import' },
  { id: 'duplicates', label: 'Dubletten prüfen', to: '/kontakte/dubletten' },
  { id: 'segments', label: 'Segmente', to: '/segmente' },
  { id: 'segment-new', label: 'Neues Segment', to: '/segmente/neu' },
  { id: 'campaigns', label: 'Kampagnen', to: '/kampagnen' },
  { id: 'campaign-new', label: 'Neue Kampagne', to: '/kampagnen/neu' },
  { id: 'journeys', label: 'Journeys', to: '/journeys' },
  { id: 'crm', label: 'CRM — Verkaufstafel', to: '/crm' },
  { id: 'tasks', label: 'Aufgaben', to: '/crm/aufgaben' },
  { id: 'analytics', label: 'Auswertung', to: '/auswertung' },
  {
    id: 'deliverability',
    label: 'Zustellbarkeit',
    hint: 'SPF, DKIM, DMARC',
    to: '/zustellbarkeit',
  },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState('');

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const activeEl = document.activeElement;
      const isEditing = activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || (activeEl as HTMLElement).isContentEditable);

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setInitialQuery('');
        setOpen((current) => !current);
        return;
      }

      if (event.key === '/' && !isEditing && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        setInitialQuery('/');
        setOpen(true);
        return;
      }

      if (event.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!open) return null;
  return <PaletteDialog initialQuery={initialQuery} onClose={() => setOpen(false)} />;
}

function PaletteDialog({ initialQuery = '', onClose }: { initialQuery?: string; onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);
  const [aktiv, setAktiv] = useState(0);

  const debounced = useDebounced(query, 200);
  const kontakte = useQuery({
    ...contactsQuery({ search: debounced.startsWith('/') ? '' : debounced }),
    enabled: !debounced.startsWith('/') && debounced.trim().length >= 2,
  });

  const treffer = useMemo(() => {
    const suche = query.trim().toLowerCase();

    if (suche.startsWith('/')) {
      const cmdSearch = suche.slice(1);
      const matchedCmds = SLASH_COMMANDS.filter(
        (c) => c.command.toLowerCase().includes(suche) || c.label.toLowerCase().includes(cmdSearch) || c.description.toLowerCase().includes(cmdSearch),
      );
      return matchedCmds.map((c) => ({
        id: `slash-${c.command}`,
        label: `${c.command} — ${c.label}`,
        hint: c.description,
        to: c.to,
        params: c.params,
      }));
    }

    const orte = suche
      ? ORTE.filter(
          (ort) =>
            ort.label.toLowerCase().includes(suche) || ort.hint?.toLowerCase().includes(suche),
        )
      : ORTE;

    const personen: Ziel[] = (kontakte.data?.items ?? []).slice(0, 5).map((contact) => ({
      id: `contact-${contact.id}`,
      label: contact.email,
      hint: [contact.firstName, contact.lastName].filter(Boolean).join(' ') || 'Kontakt',
      to: '/kontakte/$id',
      params: { id: contact.id },
    }));

    return [...orte, ...personen];
  }, [query, kontakte.data]);

  function waehlen(ziel: Ziel) {
    onClose();
    void navigate(
      ziel.params
        ? { to: ziel.to as '/kontakte/$id', params: ziel.params as { id: string } }
        : { to: ziel.to },
    );
  }

  function onInputKey(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setAktiv((i) => Math.min(i + 1, treffer.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setAktiv((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const ziel = treffer[aktiv];
      if (ziel) waehlen(ziel);
    }
  }

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="palette" role="dialog" aria-modal="true" aria-label="Schnellzugriff">
        <input
          // Frisch gemountet — autoFocus greift genau einmal, beim Öffnen.
          autoFocus
          className="palette__input"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setAktiv(0);
          }}
          onKeyDown={onInputKey}
          placeholder="Befehl eingeben (z. B. /kampagne, /kontakt) oder suchen …"
          aria-label="Suche"
          role="combobox"
          aria-expanded="true"
          aria-controls="palette-list"
        />

        <ul className="palette__list" id="palette-list" role="listbox">
          {treffer.length === 0 && <li className="palette__empty">Nichts gefunden.</li>}
          {treffer.map((ziel, index) => (
            <li key={ziel.id} role="option" aria-selected={index === aktiv}>
              <button
                type="button"
                data-active={index === aktiv ? 'true' : undefined}
                onMouseEnter={() => setAktiv(index)}
                onClick={() => waehlen(ziel)}
              >
                <span>{ziel.label}</span>
                {ziel.hint && <span className="palette__hint">{ziel.hint}</span>}
              </button>
            </li>
          ))}
        </ul>

        <footer className="palette__footer">
          <kbd>/</kbd> Befehle · <kbd>↑</kbd> <kbd>↓</kbd> wählen · <kbd>Enter</kbd> öffnen · <kbd>Esc</kbd> schließen
        </footer>
      </div>
    </div>
  );
}
